import React, { useEffect, useState, useRef } from 'react';
import { withRouter, useHistory } from 'react-router-dom';
import { TOUR_PENDING_KEY } from '../Shared/WhatsNew/tours';
import { resumeOnboardingProducts } from '../Shared/WhatsNew/onboardingTour';

import { productsTableConfigMain } from '../config';
import { productsStyles } from '../styles/ProductsStyles';
import { generalStyles } from '../styles/GeneralStyles';

import {
  Tabs,
  Tab,
  Paper,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  TablePagination,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import Row from './ProductTable/Row';
import TableToolBar from './ProductTable/TableToolBar';
import SuperSearchBar from './SuperSearchBar';
import MergeDialog from './MergeDialog';
import PrintProducts from './PrintQueue/PrintProducts';
import DuplicateMergeReview from '../Admin/DuplicateDetection/DuplicateMergeReview';

import {
  superSearch,
  advancedSearch,
  getDuplicateFlags,
  getMergeCandidates,
  mergeCandidates,
  unmergeCandidates,
  updateDuplicateCandidateStatus,
} from '../../Actions';
import { sortObjectsByAttribute } from '../../Util/utilsFunctions';

// Field keys must match the server whitelist (api-search controller FIELD_COLUMNS).
// MAIN_FIELDS mirror the legacy product search's single-line layout/order so
// existing users keep their muscle memory; ADVANCED_FIELDS are the less-used
// columns tucked into an accordion (also like the legacy page).
const MAIN_FIELDS = [
  { value: 'manufacturer', label: 'Manufacturer' },
  { value: 'distributor', label: 'Distributor' },
  { value: 'tradeName', label: 'Style Name' },
  { value: 'style', label: 'Style Number' },
  { value: 'sku', label: 'SKU' },
  { value: 'materialClass', label: 'Material' },
  { value: 'color', label: 'Color' },
];
const ADVANCED_FIELDS = [
  { value: 'collection', label: 'Collection' },
  { value: 'displayName', label: 'Display Name' },
  { value: 'privateStyleName', label: 'Private Style Name' },
  { value: 'privateStyleNumber', label: 'Private Style Number' },
  { value: 'colorNumber', label: 'Color Number' },
  { value: 'materialSubClass', label: 'Material Sub Class' },
];
const FIELD_OPTIONS = [...MAIN_FIELDS, ...ADVANCED_FIELDS];

const ProductSearch = ({ location, history }) => {
  const routerHistory = useHistory();
  const productsClasses = productsStyles();
  const generalClasses = generalStyles();

  const [tab, setTab] = useState('search');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [capped, setCapped] = useState(false);
  const [total, setTotal] = useState(0);
  const [searched, setSearched] = useState(false);

  // advanced state — one value per field (like the original search), keyed by
  // the server field name. A leading "-" on a value means "not contains".
  const [fieldValues, setFieldValues] = useState({});
  const setFieldValue = (key, val) =>
    setFieldValues((v) => ({ ...v, [key]: val }));
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [printedOnly, setPrintedOnly] = useState(false);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [discontinuedOnly, setDiscontinuedOnly] = useState(false);
  const [rpoOnly, setRpoOnly] = useState(false);
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');

  // table state (local — this page is independent of the legacy search context)
  const [selected, setSelected] = useState([]);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('manufacturer_name');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  // per-column header filters (client-side narrowing of the returned results)
  const [columnFilters, setColumnFilters] = useState({});

  // merge + duplicate-review + print-queue state (parity with the legacy page)
  const [duplicateFlags, setDuplicateFlags] = useState({});
  const [dupDialogOpen, setDupDialogOpen] = useState(false);
  const [dupCandidate, setDupCandidate] = useState(null);
  const [dupProductA, setDupProductA] = useState(null);
  const [dupProductB, setDupProductB] = useState(null);
  const [dupKeeperUuid, setDupKeeperUuid] = useState('');
  const [dupMerging, setDupMerging] = useState(false);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeList, setMergeList] = useState([]);

  // Re-runs whatever search produced the current results (used after a merge).
  const lastSearchRef = useRef(null);
  const rerun = () => lastSearchRef.current && lastSearchRef.current();

  const setColumnFilter = (id, value) => {
    setColumnFilters((f) => ({ ...f, [id]: value }));
    setPage(0);
  };

  // Client-side per-column narrowing of the already-returned results.
  const filtered = results.filter((row) =>
    productsTableConfigMain.every((col) => {
      const term = (columnFilters[col.id] || '').trim().toLowerCase();
      if (!term || col.id === 'update') return true;
      return (row[col.id] ?? '').toString().toLowerCase().includes(term);
    })
  );

  const runSuper = async (q) => {
    if (!q) return;
    lastSearchRef.current = () => runSuper(q);
    setLoading(true);
    setSelected([]);
    setPage(0);
    setColumnFilters({});
    try {
      const res = await superSearch({ q });
      setResults(res.data.data || []);
      setCapped(!!res.data.capped);
      setTotal(res.data.total ?? (res.data.data || []).length);
    } catch (err) {
      setResults([]);
      setCapped(false);
      setTotal(0);
    } finally {
      setSearched(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sessionStorage.getItem(TOUR_PENDING_KEY) === 'onboarding-products') {
      sessionStorage.removeItem(TOUR_PENDING_KEY);
      setTimeout(() => resumeOnboardingProducts(routerHistory), 500);
    }
  }, []);

  // Run a super search passed via ?q= (e.g. from the home page bar).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('q');
    if (q) {
      setTab('super');
      runSuper(q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Load duplicate-candidate flags for the current results (drives the warning
  // icon on rows that have a pending potential duplicate).
  useEffect(() => {
    const uuids = results.map((p) => p.product_uuid).filter(Boolean);
    if (!uuids.length) {
      setDuplicateFlags({});
      return;
    }
    getDuplicateFlags(uuids)
      .then((res) => setDuplicateFlags(res.data.flags || {}))
      .catch(() => setDuplicateFlags({}));
  }, [results]);

  // Turn the filled fields into clauses. A leading "-" negates (not contains);
  // everything else is a plain contains. All fields are AND'd, like the original.
  const buildClauses = () =>
    FIELD_OPTIONS.map((f) => {
      const raw = (fieldValues[f.value] || '').trim();
      if (!raw) return null;
      const negate = raw.startsWith('-') && raw.length > 1;
      return {
        field: f.value,
        op: negate ? 'notContains' : 'contains',
        value: negate ? raw.slice(1).trim() : raw,
      };
    }).filter(Boolean);

  const runAdvanced = async (e) => {
    if (e) e.preventDefault();
    const clauses = buildClauses();
    // Allow a flag-only search (e.g. just "Printed only"). "Include deleted" is a
    // modifier, so it alone doesn't count as intent.
    const hasIntent =
      clauses.length > 0 ||
      printedOnly ||
      discontinuedOnly ||
      rpoOnly ||
      minPrice !== '' ||
      maxPrice !== '';
    if (!hasIntent) return;
    lastSearchRef.current = () => runAdvanced();
    setLoading(true);
    setSelected([]);
    setPage(0);
    setColumnFilters({});
    try {
      const res = await advancedSearch({
        clauses,
        match: 'all',
        printed: printedOnly ? true : undefined,
        includeDeleted: includeDeleted ? true : undefined,
        discontinued: discontinuedOnly ? true : undefined,
        rpo: rpoOnly ? true : undefined,
        minPrice: minPrice !== '' ? minPrice : undefined,
        maxPrice: maxPrice !== '' ? maxPrice : undefined,
      });
      setResults(res.data.data || []);
      setCapped(!!res.data.capped);
      setTotal(res.data.total ?? (res.data.data || []).length);
    } catch (err) {
      setResults([]);
      setCapped(false);
      setTotal(0);
    } finally {
      setSearched(true);
      setLoading(false);
    }
  };

  const clearAdvanced = () => {
    setFieldValues({});
    setMinPrice('');
    setMaxPrice('');
    setPrintedOnly(false);
    setDiscontinuedOnly(false);
    setRpoOnly(false);
    setIncludeDeleted(false);
  };

  // ---- duplicate review (warning icon → side-by-side review dialog) ----
  const handleDuplicateClick = (flag) => {
    getMergeCandidates([flag.product_uuid_a, flag.product_uuid_b]).then((res) => {
      const list = res.data.data || [];
      const pA =
        list.find((p) => p.product_uuid === flag.product_uuid_a) || list[0];
      const pB =
        list.find((p) => p.product_uuid === flag.product_uuid_b) || list[1];
      setDupProductA(pA);
      setDupProductB(pB);
      setDupCandidate(flag);
      setDupKeeperUuid('');
      setDupDialogOpen(true);
    });
  };

  const handleDupClose = () => {
    setDupDialogOpen(false);
    setDupCandidate(null);
    setDupProductA(null);
    setDupProductB(null);
    setDupKeeperUuid('');
  };

  const removeFlagsFor = (cand) =>
    setDuplicateFlags((prev) => {
      const next = { ...prev };
      delete next[cand.product_uuid_a];
      delete next[cand.product_uuid_b];
      return next;
    });

  const handleDupDismiss = () => {
    if (!dupCandidate) return;
    updateDuplicateCandidateStatus(dupCandidate.id, 'dismissed').then(() => {
      removeFlagsFor(dupCandidate);
      handleDupClose();
    });
  };

  const handleDupMergeConfirm = () => {
    if (!dupKeeperUuid || !dupCandidate) return;
    const loserUuid =
      dupKeeperUuid === dupCandidate.product_uuid_a
        ? dupCandidate.product_uuid_b
        : dupCandidate.product_uuid_a;
    setDupMerging(true);
    mergeCandidates(dupKeeperUuid, [loserUuid])
      .then(() => updateDuplicateCandidateStatus(dupCandidate.id, 'merged'))
      .then(() => {
        removeFlagsFor(dupCandidate);
        handleDupClose();
        rerun();
      })
      .finally(() => setDupMerging(false));
  };

  // ---- merge options (select rows → "Merge Options" in the toolbar) ----
  const showMergeList = () => {
    getMergeCandidates(selected).then((result) => {
      setMergeList(result.data.data);
      setMergeDialogOpen(true);
    });
  };

  const handleMergeClose = () => setMergeDialogOpen(false);

  const handleMergeFromDialog = (keeperUuid, loserUuids) =>
    mergeCandidates(keeperUuid, loserUuids).then(() => {
      setMergeDialogOpen(false);
      setMergeList([]);
      setSelected([]);
      rerun();
    });

  const handleUnmergeFromDialog = (uuids) =>
    unmergeCandidates(uuids)
      .then(() => getMergeCandidates(selected))
      .then((result) => setMergeList(result.data.data));

  // table handlers
  const isSelected = (id) => selected.indexOf(id) !== -1;
  const handleClick = (event, id) => {
    const idx = selected.indexOf(id);
    if (idx === -1) setSelected((s) => [...s, id]);
    else setSelected((s) => s.filter((x) => x !== id));
  };
  const handleSelectAllClick = (event) => {
    if (event.target.checked) setSelected(filtered.map((r) => r.product_uuid));
    else setSelected([]);
  };
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const paginated = [...filtered]
    .sort((a, b) => sortObjectsByAttribute(a, b, order, orderBy))
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const renderResults = () => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <CircularProgress />
        </div>
      );
    }
    if (!searched) return null;
    if (!results.length) {
      return (
        <Paper style={{ padding: 24, marginTop: 16 }}>
          <Typography align="center" variant="h6">
            No products matched your search.
          </Typography>
        </Paper>
      );
    }
    return (
      <Paper style={{ marginTop: 16 }}>
        {capped && (
          <Typography
            variant="body2"
            style={{ padding: '8px 16px', color: '#a15c00' }}
          >
            Showing {results.length} of {total} matches — refine your search to
            narrow results.
          </Typography>
        )}
        <TableToolBar
          productIdList={selected}
          numSelected={selected.length}
          showMergeList={showMergeList}
          refresh={selected.length}
        />
        <Typography variant="caption" style={{ padding: '0 16px', color: '#777' }}>
          Type under any column header to filter the results below.
        </Typography>
        <TableContainer>
          <Table stickyHeader aria-label="search results">
            <TableHead className="select-all-products">
              <TableRow>
                <TableCell className="select-all-checkbox" padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selected.length > 0 && selected.length < filtered.length
                    }
                    checked={
                      filtered.length > 0 && selected.length === filtered.length
                    }
                    onChange={handleSelectAllClick}
                    style={{ color: '#F4F9E9' }}
                  />
                </TableCell>
                <TableCell className="table-head">More Details</TableCell>
                {productsTableConfigMain.map((col) => (
                  <TableCell
                    key={col.id}
                    className="table-head"
                    sortDirection={orderBy === col.id ? order : false}
                  >
                    {col.id === 'update' ? (
                      col.label
                    ) : (
                      <TableSortLabel
                        active={orderBy === col.id}
                        direction={orderBy === col.id ? order : 'asc'}
                        onClick={(e) => handleRequestSort(e, col.id)}
                      >
                        {col.label}
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell className="select-all-checkbox" padding="checkbox" />
                <TableCell className="table-head" />
                {productsTableConfigMain.map((col) => (
                  <TableCell key={col.id + '_filter'} className="table-head">
                    {col.id === 'update' ? null : (
                      <TextField
                        value={columnFilters[col.id] || ''}
                        onChange={(e) => setColumnFilter(col.id, e.target.value)}
                        placeholder="Filter"
                        size="small"
                        variant="outlined"
                        style={{
                          backgroundColor: '#fff',
                          borderRadius: 4,
                          minWidth: 90,
                        }}
                      />
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.map((product, i) => (
                <Row
                  key={'search_' + product.product_uuid + '_' + product.style}
                  product={product}
                  rowNumber={i + 1}
                  isItemSelected={isSelected(product.product_uuid)}
                  labelId={`search-checkbox-${product.product_uuid}`}
                  handleClick={handleClick}
                  duplicateFlag={duplicateFlags[product.product_uuid]}
                  onDuplicateClick={handleDuplicateClick}
                  hasPrinted={!!product.has_been_printed}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100, 500]}
          component="div"
          count={filtered.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    );
  };

  return (
    <div className={productsClasses.productsContainerFull}>
      <Paper style={{ padding: 16 }}>
        <div id="tour-search-header" style={{ position: 'relative' }}>
          <Typography variant="h4" className={generalClasses.header}>
            Product Search
          </Typography>
          {/* Print-queue widget: floated over the right of the full-width banner */}
          <div
            style={{
              position: 'absolute',
              top: '50%',
              right: 12,
              transform: 'translateY(-50%)',
            }}
          >
            <PrintProducts open={false} buttonLabel="Print Queue" />
          </div>
        </div>

        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Search" value="search" />
          <Tab label="Super Search" value="super" />
        </Tabs>

        {tab === 'super' && (
          <div id="tour-super-search" style={{ marginTop: 16 }}>
            <SuperSearchBar
              autoFocus
              initialValue={new URLSearchParams(location.search).get('q') || ''}
              onSearch={runSuper}
            />
            <Typography
              variant="caption"
              style={{ display: 'block', marginTop: 8, color: '#777' }}
            >
              Searches every field at once. Tip: start a word with “-” to exclude
              it (e.g. “-gray”), or use “quotes” for an exact phrase.
            </Typography>
          </div>
        )}

        {tab === 'search' && (
          <form onSubmit={runAdvanced} style={{ marginTop: 16 }} id="tour-field-search">
            {/* Main, most-used fields on a single line — mirrors the legacy page */}
            <div>
              {MAIN_FIELDS.map((f) => (
                <TextField
                  key={f.value}
                  className={productsClasses.tableFilter}
                  label={f.label}
                  type="search"
                  variant="filled"
                  value={fieldValues[f.value] || ''}
                  onChange={(e) => setFieldValue(f.value, e.target.value)}
                />
              ))}
            </div>

            {/* Small toggle to reveal the lesser-used columns inline */}
            <Button
              id="tour-advanced-search"
              size="small"
              onClick={() => setShowAdvanced((s) => !s)}
              endIcon={
                <ExpandMoreIcon
                  style={{
                    transform: showAdvanced ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.2s',
                  }}
                />
              }
              style={{ margin: '6px 0', color: '#ff682c' }}
            >
              Advanced Search
            </Button>

            {/* Extra fields, between the main fields and the Search/Clear buttons */}
            {showAdvanced && (
              <div style={{ marginBottom: 8 }}>
                <div>
                  {ADVANCED_FIELDS.map((f) => (
                    <TextField
                      key={f.value}
                      className={productsClasses.tableFilter}
                      label={f.label}
                      type="search"
                      variant="filled"
                      value={fieldValues[f.value] || ''}
                      onChange={(e) => setFieldValue(f.value, e.target.value)}
                    />
                  ))}
                  <TextField
                    className={productsClasses.tableFilter}
                    label="Min Price"
                    type="search"
                    variant="filled"
                    value={minPrice}
                    onChange={(e) => setMinPrice(e.target.value)}
                  />
                  <TextField
                    className={productsClasses.tableFilter}
                    label="Max Price"
                    type="search"
                    variant="filled"
                    value={maxPrice}
                    onChange={(e) => setMaxPrice(e.target.value)}
                  />
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 16,
                    alignItems: 'center',
                    marginTop: 8,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={rpoOnly}
                        onChange={(e) => setRpoOnly(e.target.checked)}
                      />
                    }
                    label="Retail Price Override"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={discontinuedOnly}
                        onChange={(e) => setDiscontinuedOnly(e.target.checked)}
                      />
                    }
                    label="Discontinued"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={printedOnly}
                        onChange={(e) => setPrintedOnly(e.target.checked)}
                      />
                    }
                    label="Printed only"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includeDeleted}
                        onChange={(e) => setIncludeDeleted(e.target.checked)}
                      />
                    }
                    label="Include deleted"
                  />
                </div>
                <Typography
                  variant="caption"
                  style={{ display: 'block', marginTop: 8, color: '#777' }}
                >
                  Tip: start any value with “-” to exclude it (e.g. “-gray”).
                </Typography>
              </div>
            )}

            <div style={{ marginTop: 8 }}>
              <Button
                type="submit"
                variant="contained"
                className={productsClasses.productFilterButton}
              >
                Search
              </Button>
              <Button
                onClick={clearAdvanced}
                variant="contained"
                className={productsClasses.productFilterButton}
              >
                Clear
              </Button>
            </div>
          </form>
        )}
      </Paper>

      {renderResults()}

      <MergeDialog
        open={mergeDialogOpen}
        productList={mergeList}
        onMerge={handleMergeFromDialog}
        onUnmerge={handleUnmergeFromDialog}
        onClose={handleMergeClose}
      />

      <Dialog open={dupDialogOpen} onClose={handleDupClose} fullScreen>
        <DialogTitle
          disableTypography
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingRight: 16,
          }}
        >
          <Typography variant="h6">Review Potential Duplicate</Typography>
          <Button
            variant="outlined"
            style={{ minWidth: 36, padding: '4px 8px' }}
            onClick={handleDupClose}
          >
            X
          </Button>
        </DialogTitle>
        <DialogContent dividers>
          {dupProductA && dupProductB && (
            <DuplicateMergeReview
              productA={dupProductA}
              productB={dupProductB}
              keeperUuid={dupKeeperUuid}
              onKeep={setDupKeeperUuid}
              mode="merge"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleDupClose} disabled={dupMerging}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={handleDupDismiss}
            disabled={dupMerging}
          >
            Not a Duplicate
          </Button>
          <Button
            variant="contained"
            style={{ backgroundColor: '#FF682C', color: '#fff' }}
            onClick={handleDupMergeConfirm}
            disabled={!dupKeeperUuid || dupMerging}
          >
            {dupMerging ? (
              <>
                <CircularProgress
                  size={16}
                  style={{ color: '#fff', marginRight: 8 }}
                />
                Merging…
              </>
            ) : dupKeeperUuid ? (
              'Confirm Merge'
            ) : (
              'Select a product to keep'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default withRouter(ProductSearch);
